import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  useAccountSessions,
  useConnectedApps,
  useLogOutOtherSessions,
  useRevokeConnectedApp,
  useUpdateAccount,
} from '@/api/account'
import type { AccountSession, ConnectedApp } from '@/api/account'
import { useMe } from '@/api/me'
import type { ApiError } from '@/api/me'
import { useLogOut } from '@/components/app-shell/use-log-out'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// The signed-in user's own Account page (UI-SCREENS A-10, #47), Owner and Staff alike: name,
// email, sign-in methods, active sessions, log out other devices, connected AI apps (A-12), log out.
export const Route = createFileRoute('/w/$slug/account')({
  component: AccountPage,
  staticData: { breadcrumb: 'account.title' },
})

function AccountPage() {
  const { t } = useTranslation()
  const { onLogOut, loggingOut, logoutFailed } = useLogOut()

  return (
    <section className="flex max-w-xl flex-col gap-4">
      <h1 className="font-heading text-xl font-semibold">{t('account.title')}</h1>
      <ProfileCard />
      <SignInMethodsCard />
      <SessionsCard />
      <ConnectedAppsCard />
      <div className="flex flex-col gap-2">
        <p role="alert" className="text-sm text-destructive empty:hidden">
          {logoutFailed ? t('shell.logoutFailed') : ''}
        </p>
        <Button variant="outline" className="h-11 self-start" disabled={loggingOut} onClick={onLogOut}>
          {t(loggingOut ? 'shell.loggingOut' : 'shell.logout')}
        </Button>
      </div>
    </section>
  )
}

function ProfileCard() {
  const { t } = useTranslation()
  // Already loaded by the workspace layout.
  const user = useMe().data?.user

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('account.profile.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {user ? <NameForm name={user.name} /> : null}
        <dl className="flex flex-col gap-1">
          <dt className="text-sm font-medium">{t('account.profile.email')}</dt>
          <dd className="text-base break-all">{user?.email}</dd>
        </dl>
      </CardContent>
    </Card>
  )
}

function NameForm({ name }: { name: string | null }) {
  const { t } = useTranslation()
  const updateAccount = useUpdateAccount()

  const formSchema = z.object({
    name: z.string().max(100, { message: t('account.profile.nameTooLong') }),
  })
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: name ?? '' },
  })

  const onSubmit = form.handleSubmit((values) => {
    updateAccount.mutate(
      { body: { account: { name: values.name } } },
      { onSuccess: (data) => form.reset({ name: data.name ?? '' }) },
    )
  })

  const fieldError = form.formState.errors.name?.message ?? ''
  const requestError = updateAccount.isError ? errorMessage(t, updateAccount.error) : ''

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {updateAccount.isSuccess && !form.formState.isDirty ? t('account.profile.saved') : ''}
      </p>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {requestError}
      </p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="account-name">{t('account.profile.name')}</Label>
        <Input
          id="account-name"
          className="h-11"
          autoComplete="name"
          aria-invalid={Boolean(fieldError)}
          aria-describedby="account-name-hint account-name-error"
          {...form.register('name')}
        />
        <p id="account-name-hint" className="text-sm text-muted-foreground">
          {t('account.profile.nameHint')}
        </p>
        <p id="account-name-error" role="alert" className="text-sm text-destructive empty:hidden">
          {fieldError}
        </p>
      </div>
      <Button type="submit" className="h-11 self-start" disabled={updateAccount.isPending}>
        {t(updateAccount.isPending ? 'account.profile.saving' : 'account.profile.save')}
      </Button>
    </form>
  )
}

// Magic link works for everyone. Google shows as linked once the user has signed in with it
// (the first Google sign-in links the account by email, #8).
function SignInMethodsCard() {
  const { t } = useTranslation()
  // Already loaded by the workspace layout.
  const googleLinked = useMe().data?.user.google_linked ?? false

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('account.signInMethods.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-2 text-sm">
          <li className="flex items-center justify-between gap-2">
            <span>{t('account.signInMethods.magicLink')}</span>
            <Badge variant="secondary">{t('account.signInMethods.active')}</Badge>
          </li>
          <li className="flex items-center justify-between gap-2">
            <span className="flex flex-col">
              <span>{t('account.signInMethods.google')}</span>
              {googleLinked ? null : (
                <span className="text-muted-foreground">{t('account.signInMethods.googleHint')}</span>
              )}
            </span>
            <Badge variant={googleLinked ? 'secondary' : 'outline'}>
              {t(googleLinked ? 'account.signInMethods.linked' : 'account.signInMethods.notLinked')}
            </Badge>
          </li>
        </ul>
      </CardContent>
    </Card>
  )
}

function SessionsCard() {
  const { t } = useTranslation()
  const sessions = useAccountSessions()
  const logOutOthers = useLogOutOtherSessions()
  const hasOthers = sessions.data?.some((session) => !session.current) ?? false

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('account.sessions.title')}</CardTitle>
        <CardDescription>{t('account.sessions.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {sessions.isPending ? (
          <LoadingSkeleton variant="form" rows={4} />
        ) : sessions.isError || !sessions.data ? (
          <p role="alert" className="text-sm text-destructive">
            {t('errors.generic')}
          </p>
        ) : (
          <ul className="flex flex-col divide-y">
            {sessions.data.map((session) => (
              <SessionRow key={session.id} session={session} />
            ))}
          </ul>
        )}

        <p role="status" aria-live="polite" className="text-sm empty:hidden">
          {logOutOthers.isSuccess ? t('account.sessions.othersLoggedOut') : ''}
        </p>
        <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
          {logOutOthers.isError ? t('errors.generic') : ''}
        </p>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="h-11 self-start" disabled={!hasOthers || logOutOthers.isPending}>
              {t('account.sessions.logOutOthers')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('account.sessions.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('account.sessions.confirmBody')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('account.sessions.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={() => logOutOthers.mutate({})}>
                {t('account.sessions.logOutOthers')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

function SessionRow({ session }: { session: AccountSession }) {
  const { t } = useTranslation()

  return (
    <li className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{deviceLabel(session.user_agent) ?? t('account.sessions.unknownDevice')}</span>
        {session.current ? <Badge>{t('account.sessions.current')}</Badge> : null}
      </div>
      <span className="text-sm text-muted-foreground">
        {t('account.sessions.details', { ip: session.ip, lastSeen: formatDateTime(session.last_seen_at) })}
      </span>
    </li>
  )
}

// MCP clients (ChatGPT, Claude…) connected through OAuth (ADR 0004). Only an Owner can connect
// one; the server URL to paste into the client is shown here.
function ConnectedAppsCard() {
  const { t } = useTranslation()
  const apps = useConnectedApps()
  const revoke = useRevokeConnectedApp()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('account.connectedApps.title')}</CardTitle>
        <CardDescription>{t('account.connectedApps.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="flex flex-col gap-1">
          <dt className="text-sm font-medium">{t('account.connectedApps.serverUrl')}</dt>
          <dd className="font-mono text-sm break-all">{`${window.location.origin}/mcp`}</dd>
        </dl>
        {apps.isPending ? (
          <LoadingSkeleton variant="form" rows={2} />
        ) : apps.isError || !apps.data ? (
          <p role="alert" className="text-sm text-destructive">
            {t('errors.generic')}
          </p>
        ) : apps.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('account.connectedApps.empty')}</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {apps.data.map((app) => (
              <ConnectedAppRow
                key={`${app.id}-${app.workspace_slug}`}
                app={app}
                disabled={revoke.isPending}
                onRevoke={() => revoke.mutate({ params: { path: { id: app.id } } })}
              />
            ))}
          </ul>
        )}
        <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
          {revoke.isError ? t('errors.generic') : ''}
        </p>
      </CardContent>
    </Card>
  )
}

function ConnectedAppRow({ app, disabled, onRevoke }: { app: ConnectedApp; disabled: boolean; onRevoke: () => void }) {
  const { t } = useTranslation()

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
      <span className="flex flex-col gap-1">
        <span className="font-medium">{app.name}</span>
        <span className="text-sm text-muted-foreground">
          {t('account.connectedApps.details', {
            workspace: app.workspace_name,
            connectedAt: formatDateTime(app.connected_at),
          })}
        </span>
      </span>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" className="h-11" disabled={disabled}>
            {t('account.connectedApps.revoke')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('account.connectedApps.confirmTitle', { name: app.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t('account.connectedApps.confirmBody', { name: app.name })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('account.sessions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onRevoke}>{t('account.connectedApps.revoke')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

// "Chrome di Android" style label from the user agent; null when neither part is recognised.
function deviceLabel(userAgent: string | null) {
  if (!userAgent) return null
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Firefox\//.test(userAgent)
      ? 'Firefox'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : null
  const os = /Android/.test(userAgent)
    ? 'Android'
    : /iPhone/.test(userAgent)
      ? 'iPhone'
      : /iPad/.test(userAgent)
        ? 'iPad'
        : /Windows/.test(userAgent)
          ? 'Windows'
          : /Mac OS X/.test(userAgent)
            ? 'macOS'
            : /Linux/.test(userAgent)
              ? 'Linux'
              : null
  if (browser && os) return `${browser} · ${os}`
  return browser ?? os
}

function formatDateTime(iso: string) {
  return new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))
}

// Server errors are translated by their stable key (ARCHITECTURE API errors).
function errorMessage(t: (key: string) => string, error: unknown) {
  const key = (error as ApiError | undefined)?.error?.key
  return key === 'validation_failed' ? t('account.profile.invalid') : t('errors.generic')
}
